import type { AudioOutputDiagnostics } from '@/lib/audio-output-diagnostics';
import {
  type CallDiagnosticsData,
  type MediaTimeoutDiagnosticsData,
  flattenAudioOutputDiagnostics,
  flattenCallDiagnostics,
  flattenMediaTimeoutDiagnostics,
} from '@/services/logger/call-diagnostics';
import { newRelicService } from '@/services/newrelic';
import { tracingService } from '@/services/tracing/tracing-service';
import * as Sentry from '@sentry/react';

export interface LogContext {
  [key: string]: any;
}

export type LogTag = 'calls' | 'vonage' | 'audio';

export type StructuredLogContext = {
  callId?: string;
  diagnostics?: CallDiagnosticsData;
  audioOutputDiagnostics?: AudioOutputDiagnostics;
  mediaTimeoutDiagnostics?: MediaTimeoutDiagnosticsData;
  [key: string]: unknown;
};

export type StructuredLogger = {
  info: (message: string, context?: StructuredLogContext) => void;
  warn: (message: string, context?: StructuredLogContext) => void;
  error: (message: string, context?: StructuredLogContext) => void;
};

type NewRelicLogLevel = 'TRACE' | 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

/** Standard log message: `[calls] [vonage] [VonageSessionManager] Missed token in response`. */
const formatLogMessage = (component: string, tags: LogTag[], message: string): string =>
  [...tags.map((tag) => `[${tag}]`), `[${component}]`, message].join(' ');

/** Boolean `tags.*` columns so New Relic can filter consistently across platforms. */
const tagAttributes = (tags: LogTag[]): Record<string, boolean> =>
  Object.fromEntries(tags.map((tag) => [`tags.${tag}`, true]));

const toCamelCase = (key: string): string =>
  key.replace(/_+([a-zA-Z0-9])/g, (_match, char: string) => char.toUpperCase());

/**
 * Shallow copy of `attributes` with every key normalized to camelCase. New Relic
 * facets logs by attribute name, so keys must be camelCase. Most payloads are
 * already camelCase at the source, but some log-only contexts still carry
 * snake_case keys (e.g. `resolution_method`); normalizing a copy here keeps every
 * New Relic attribute camelCase without forcing every call site to match.
 */
const toCamelCaseKeys = (attributes: Record<string, unknown>): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attributes)) {
    normalized[toCamelCase(key)] = value;
  }
  return normalized;
};

/**
 * Correlation ids attached to calls/vonage-tagged logs: `deviceLegId` /
 * `callTraceId` (matching the camelCase keys the backend events stream carries,
 * so logs join to events by key and value) plus the session's Vonage region and
 * SDK session id for faceting.
 */
export function callBaseAttributes(callId: string | undefined): Record<string, string> {
  const fields: Record<string, string> = {};
  if (callId) {
    fields.deviceLegId = callId;
  }
  const callTraceId = tracingService.getCallTraceId(callId);
  if (callTraceId) {
    fields.callTraceId = callTraceId;
  }
  const vonageRegion = tracingService.getVonageRegion();
  if (vonageRegion) {
    fields.vonageRegion = vonageRegion;
  }
  const vonageSessionId = tracingService.getVonageSessionId();
  if (vonageSessionId) {
    fields.vonageSessionId = vonageSessionId;
  }
  return fields;
}

const consoleStyleByMethod: Record<'log' | 'warn' | 'error', { tag: string; event: string }> = {
  log: {
    tag: 'background:#2563eb;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;',
    event: 'color:#2563eb;font-weight:bold;',
  },
  warn: {
    tag: 'background:#d97706;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;',
    event: 'color:#d97706;font-weight:bold;',
  },
  error: {
    tag: 'background:#dc2626;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;',
    event: 'color:#dc2626;font-weight:bold;',
  },
};

const logToConsoleInDev = (
  message: string,
  context: Record<string, unknown>,
  level: NewRelicLogLevel,
): void => {
  if (!import.meta.env.DEV) {
    return;
  }
  const method = level === 'ERROR' ? 'error' : level === 'WARN' ? 'warn' : 'log';
  const style = consoleStyleByMethod[method];
  console[method](`%c[LOG]%c %c${message}%c`, style.tag, '', style.event, '', context);
};

const emit = (
  component: string,
  tags: LogTag[],
  level: NewRelicLogLevel,
  message: string,
  context: StructuredLogContext = {},
  options: { sentry?: boolean; devConsole?: boolean } = {},
): void => {
  const { diagnostics, audioOutputDiagnostics, mediaTimeoutDiagnostics, ...restContext } = context;

  const formattedMessage = formatLogMessage(component, tags, message);
  const includeCallContext = tags.includes('calls') || tags.includes('vonage');
  const base = includeCallContext ? callBaseAttributes(restContext.callId) : {};

  const attributes: Record<string, unknown> = {
    component,
    ...tagAttributes(tags),
    ...restContext,
    ...base,
  };

  if (diagnostics) {
    Object.assign(attributes, flattenCallDiagnostics(diagnostics));
  }

  if (audioOutputDiagnostics) {
    Object.assign(attributes, flattenAudioOutputDiagnostics(audioOutputDiagnostics));
  }

  if (mediaTimeoutDiagnostics) {
    Object.assign(attributes, flattenMediaTimeoutDiagnostics(mediaTimeoutDiagnostics));
  }

  // Normalize keys to camelCase at the New Relic boundary. Some log-only
  // contexts still carry snake_case keys (e.g. `resolution_method`);
  // New Relic only ever sees camelCase.
  const newRelicAttributes = toCamelCaseKeys(attributes);

  newRelicService.log(formattedMessage, {
    level,
    customAttributes: newRelicAttributes,
  });

  if (options.devConsole !== false) {
    logToConsoleInDev(formattedMessage, newRelicAttributes, level);
  }

  if (options.sentry) {
    Sentry.captureException(new Error(formattedMessage), {
      level: 'error',
      extra: {
        component,
        ...restContext,
        ...base,
        diagnostics,
      },
    });
  }
};

/**
 * Structured logger. Ships `[calls] [vonage] [Component] message` to New Relic
 * with `tags.*` and `component` columns; errors are mirrored to Sentry. Keep
 * messages constant per call site — variability belongs in the context
 * attributes so NRQL can match messages exactly.
 */
export const createLogger = (component: string, tags: LogTag[]): StructuredLogger => ({
  info: (message, context) => emit(component, tags, 'INFO', message, context),
  warn: (message, context) => emit(component, tags, 'WARN', message, context),
  error: (message, context) => emit(component, tags, 'ERROR', message, context, { sentry: true }),
});

/**
 * Low-level entry point for log adapters that relay an external source (e.g.
 * the Vonage SDK custom logger) into the structured format. Supports the full
 * New Relic level range, does not mirror to Sentry (adapters own their error
 * routing), and skips the dev console (high-volume sources stay quiet locally).
 */
export const logStructured = (
  component: string,
  tags: LogTag[],
  level: NewRelicLogLevel,
  message: string,
  context?: StructuredLogContext,
): void => emit(component, tags, level, message, context, { devConsole: false });

const isSentryReady = (): boolean => {
  try {
    const client = Sentry.getClient();
    if (!client) {
      return false;
    }

    // Check if DSN is configured (required for transport)
    const options = client.getOptions();
    return !!options?.dsn;
  } catch {
    return false;
  }
};

/** Legacy untagged logger. Prefer `createLogger(component, tags)`. */
export const logger = {
  info(message: string, context?: LogContext): void {
    if (isSentryReady()) {
      Sentry.captureMessage(message, {
        level: 'info',
        extra: context,
      });
    }

    newRelicService.log(message, {
      level: 'INFO',
      customAttributes: context,
    });
  },

  warn(message: string, context?: LogContext): void {
    if (isSentryReady()) {
      Sentry.captureMessage(message, {
        level: 'warning',
        extra: context,
      });
    }

    newRelicService.log(message, {
      level: 'WARN',
      customAttributes: context,
    });
  },

  error(message: string, context?: LogContext): void {
    if (isSentryReady()) {
      Sentry.captureException(new Error(message), {
        level: 'error',
        extra: context,
      });
    }

    newRelicService.noticeError(new Error(message), context);
  },

  /**
   * Capture an exception with advanced Sentry options (fingerprinting, tags, custom level)
   * Use this when you need more control over how errors are reported to Sentry.
   *
   * @example
   * ```typescript
   * logger.captureException(error, {
   *   fingerprint: ['vonage-call-cleanup', 'session_error'],
   *   tags: { component: 'vonage_call_cleanup' },
   *   level: 'warning',
   *   context: { callId, attemptHangup }
   * });
   * ```
   */
  captureException(
    error: Error,
    options?: {
      fingerprint?: string[];
      tags?: Record<string, string>;
      level?: 'error' | 'warning' | 'info';
      context?: LogContext;
    },
  ): void {
    const { fingerprint, tags, level = 'error', context } = options || {};

    // Send to Sentry with advanced options if initialized
    if (isSentryReady()) {
      Sentry.captureException(error, {
        fingerprint,
        tags,
        level,
        extra: context,
      });
    }

    newRelicService.log(error.message, { ...context, level: 'ERROR' });
    // Send to New Relic
    newRelicService.noticeError(error, context);
  },

  child(defaultContext: LogContext) {
    return {
      info: (message: string, context?: LogContext) =>
        logger.info(message, { ...defaultContext, ...context }),
      warn: (message: string, context?: LogContext) =>
        logger.warn(message, { ...defaultContext, ...context }),
      error: (message: string, context?: LogContext) =>
        logger.error(message, { ...defaultContext, ...context }),
    };
  },
};
